import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { cx } from "./primitives";

type Toast = { id: number; text: string; tone: "success" | "danger" | "info" };
const Ctx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {toasts.map((t) => (
          <div key={t.id} role="status"
            className={cx("pointer-events-auto flex max-w-md items-center gap-2 rounded-2xl px-4 py-3 text-[15px] shadow-raised animate-[riseIn_180ms_ease-out]",
              t.tone === "danger" ? "bg-danger text-white" : t.tone === "info" ? "bg-ink text-bg" : "bg-primary text-on-primary")}>
            {t.tone === "danger" ? <AlertTriangle className="h-4 w-4" aria-hidden /> : t.tone === "info" ? <Info className="h-4 w-4" aria-hidden /> : <CheckCircle2 className="h-4 w-4" aria-hidden />}
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
